import { selectCurrentTeam } from 'containers/app/appSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'hooks/typedHooks';
import { isPermissionDenied } from 'utils/apiUtils';
import { useLazyGetCustomersListV2Query } from '../api';
import { GetCustomersV2ColumnInfo, GetCustomersV2ListItem } from '../customers.model';
import { formatCustomerFilterParamsForAPI } from '../Utils';

export interface CustomerV2QueryParams {
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  viewId?: string;
  focusedColumns?: string[];
  totalInvoicedAmount?: string;
  outstandingBalanceAmount?: string;
  totalPaidAmount?: string;
  disputeLossAmount?: string;
  refundAmount?: string;
  lastActivityAtFrom?: string;
  lastActivityAtTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  jobCount?: string;
  cardOnFileCount?: string;
  invoiceCount?: string;
  estimateCount?: string;
  createdBy?: string[];
  limit?: number;
}

export const initialQueryParamsState: CustomerV2QueryParams = {
  search: '',
  sortBy: '',
  sortOrder: ''
};

export const usePaginatedGetCustomersV2Query = (limit = 50) => {
  const [list, setList] = useState<GetCustomersV2ListItem[]>([]);
  const [queryParams, setQueryParams] = useState<CustomerV2QueryParams>(initialQueryParamsState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [columnConfig, setColumnConfig] = useState<GetCustomersV2ColumnInfo[]>([]);
  const page = useRef(1);
  const [getCustomersListQuery, customersState] = useLazyGetCustomersListV2Query();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInitListCall: boolean = page.current === 1;
  const queryParamsRef = useRef(queryParams);

  // Update ref whenever queryParams changes
  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  // Serialize queryParams to a stable string for comparison
  // This ensures the effect only runs when actual values change, not just the reference
  const queryParamsKey = useMemo(() => JSON.stringify(queryParams), [queryParams]);

  useEffect(() => {
    if (!customersState?.isFetching) {
      setHasPermissionsToView(true);

      if (customersState?.data) {
        const { customerList, columnInfo, totalCount } = customersState.data;
        setTotalListCount(totalCount);
        setList(customerList || []);

        if (columnInfo && Array.isArray(columnInfo)) {
          const sortedColumnInfo = [...columnInfo].sort((a, b) => a.priority - b.priority);
          setColumnConfig(sortedColumnInfo);
        } else {
          setColumnConfig(columnInfo);
        }
      }

      setIsLoading(false);
    }
  }, [customersState?.isFetching]);

  useEffect(() => {
    if (!customersState?.isFetching && customersState?.isError) {
      setIsLoading(false);
      if (isPermissionDenied(customersState.error?.['data']?.code, customersState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
      setIsError(true);
    }
  }, [customersState?.isError]);

  const fetchData = useCallback(
    (currentPage: number) => {
      setIsLoading(true);
      setIsError(false);
      page.current = currentPage;
      const currentQueryParams = queryParamsRef.current;
      const finalQueryParams = formatCustomerFilterParamsForAPI(currentQueryParams);
      const currentLimit = currentQueryParams.limit ?? limit;
      if (currentTeam?.id) {
        try {
          getCustomersListQuery({
            teamId: currentTeam.id.toString(),
            limit: currentLimit,
            page: page.current - 1,
            ...finalQueryParams
          }).unwrap();
        } catch (err) {
          setIsError(true);
        }
      }
    },
    [currentTeam?.id, limit, getCustomersListQuery]
  );

  // Effect to fetch data when queryParams actually change
  // Using queryParamsKey (serialized string) ensures we only run when values change
  // fetchData is stable, so it won't cause unnecessary re-runs
  useEffect(() => {
    setList([]);
    fetchData(1);
  }, [queryParamsKey, fetchData]);

  const resetQuery = useCallback(() => {
    setQueryParams(initialQueryParamsState);
  }, []);

  return {
    isLoading,
    isFetching: customersState?.isFetching,
    totalListCount,
    list,
    isError,
    fetchData,
    resetQuery,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: customersState.isSuccess,
    hasPermissionsToView,
    page: page.current,
    columnConfig
  };
};
